import { getSecondThursday } from "../util/date-helpers";
import { loadJsonFromUrl } from "../util/json-loader";

type NewslettersData = Record<string, string[]>;

const newsletterAssetsBaseUrl =
  "https://assets.richmondwoodturners.org/newsletters/";

const possibleMonths: Record<string, string> = {
  "01": "January",
  "02": "February",
  "03": "March",
  "04": "April",
  "05": "May",
  "06": "June",
  "07": "July",
  "08": "August",
  "09": "September",
  "10": "October",
  "11": "November",
  "12": "December",
};

function getNewsletterData(): Promise<NewslettersData | null> {
  return loadJsonFromUrl<NewslettersData>(
    `${newsletterAssetsBaseUrl}newsletters.json`,
  );
}

function buildLinkToNewsletter(year: string, month: string): string {
  return `${newsletterAssetsBaseUrl}${year}/${year}_${month}_newsletter.pdf`;
}

function buildNewsletterHTML(newslettersData: NewslettersData): string {
  // newslettersData is an object where keys are years and values are arrays of month data
  // Sort years in reverse order (newest first)
  const years = Object.keys(newslettersData).sort().reverse();
  const allMonths = Object.keys(possibleMonths).sort();

  return years
    .map((year) => {
      const availableMonths = newslettersData[year];

      const monthsHTML = allMonths
        .map((month) => {
          if (availableMonths.indexOf(month) >= 0) {
            return `
                    <div class="newsletter-month">
                        <a href="${buildLinkToNewsletter(
                          year,
                          month,
                        )}" target="_blank" rel="noreferrer noopener">${possibleMonths[month]}</a>
                    </div>`;
          } else {
            return `
                    <div class="newsletter-month-unavailable">
                        ${possibleMonths[month]}
                    </div>`;
          }
        })
        .join("");

      return `
        <h3 class="newsletter-year-header">${year}</h3>
        <div class="newsletter-year">
            ${monthsHTML}
        </div>`;
    })
    .join("");
}

function getLatestNewsletterUrl(
  newslettersData: NewslettersData,
): string | null {
  // Get the most recent year
  const years = Object.keys(newslettersData).sort().reverse();
  if (years.length === 0) return null;

  const latestYear = years[0];
  const latestMonth =
    newslettersData[latestYear][newslettersData[latestYear].length - 1];

  return `${newsletterAssetsBaseUrl}${latestYear}/${latestYear}_${latestMonth}_newsletter.pdf`;
}

export async function getLatestNewsletter(): Promise<string | null> {
  const newsletters = await getNewsletterData();
  if (!newsletters || Object.keys(newsletters).length === 0) return null;

  return getLatestNewsletterUrl(newsletters);
}

export function fillNewsletterArchive(selector: string): void {
  getNewsletterData().then((newsletters) => {
    if (!newsletters || Object.keys(newsletters).length === 0) {
      console.error("No newsletters data available");
      fillErrorNewsletterArchive(selector);
      return;
    }

    // Fill the archives section
    const html = buildNewsletterHTML(newsletters);
    const section = document.querySelector<HTMLElement>(selector);

    if (section) {
      section.innerHTML = html;
    }
  });
}

function fillErrorNewsletterArchive(selector: string): void {
  const section = document.querySelector<HTMLElement>(selector);
  if (section) {
    section.innerHTML =
      "<p>Unable to load newsletter archives. Please try again later.</p>";
  }
}

export function fillNextNewsletterDeadline(selector: string): void {
  // The newsletter deadline in the second Thursday of each month.
  // We want to return the second Thursday of the current month unless
  // that date has already passed, in which case we return the second
  // Thursday of the next month.
  const today = new Date();
  let year = today.getFullYear();
  let month = today.getMonth(); // 0-based

  // Get the second Thursday of the current month
  let deadline = getSecondThursday(
    `${year}-${(month + 1).toString().padStart(2, "0")}`,
  );

  // If the deadline has already passed, move to next month
  if (deadline && deadline < today) {
    month++;
    if (month > 11) {
      month = 0;
      year++;
    }
    deadline = getSecondThursday(
      `${year}-${(month + 1).toString().padStart(2, "0")}`,
    );
  }

  if (!deadline) {
    console.error("Could not determine next newsletter deadline.");
    return;
  }

  const containers = document.querySelectorAll<HTMLElement>(selector);
  if (containers) {
    containers.forEach((el) => {
      el.innerText = deadline.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    });
  }
}

export function updateLatestNewsletterHref(query: string): void {
  getNewsletterData().then((newsletters) => {
    if (!newsletters || Object.keys(newsletters).length === 0) {
      console.error("No newsletters data available");
      return;
    }

    const latest = getLatestNewsletterUrl(newsletters);
    if (latest) {
      const latestLinks = document.querySelectorAll<HTMLAnchorElement>(query);
      for (let i = 0; i < latestLinks.length; i++) {
        latestLinks[i].href = latest;
      }
    }
  });
}
